import React, { useEffect, useState } from 'react';
import { useAvatar } from '../context/AvatarContext';
export const CustomCursor = ({
  position,
  cursorType
}) => {
  const {
    avatar
  } = useAvatar();
  const [cursorSize, setCursorSize] = useState({
    width: 24,
    height: 24
  });
  // Adjust cursor size based on type
  useEffect(() => {
    switch (cursorType) {
      case 'text':
        setCursorSize({
          width: 12,
          height: 12
        });
        break;
      case 'button':
        setCursorSize({
          width: 20,
          height: 20
        });
        break;
      case 'image':
        setCursorSize({
          width: 30,
          height: 30
        });
        break;
      case 'not-allowed':
        setCursorSize({
          width: 24,
          height: 24
        });
        break;
      default:
        setCursorSize({
          width: 24,
          height: 24
        });
    }
  }, [cursorType]);
  // Get avatar-specific cursor styles
  const getCursorStyles = () => {
    if (!avatar) return {};
    // Base styles
    const baseStyles = {
      borderColor: avatar.accent,
      boxShadow: `0 0 15px ${avatar.accent}80`
    };
    // Special case for not-allowed cursor
    if (cursorType === 'not-allowed') {
      return {
        ...baseStyles,
        borderRadius: '50%',
        border: `2px solid #ff0000`,
        boxShadow: `0 0 15px #ff000080`,
        background: 'rgba(255, 0, 0, 0.1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '2px',
          height: '100%',
          background: '#ff0000',
          left: '50%',
          transform: 'translateX(-50%) rotate(45deg)'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '2px',
          height: '100%',
          background: '#ff0000',
          left: '50%',
          transform: 'translateX(-50%) rotate(-45deg)'
        }
      };
    }
    // Avatar-specific styles
    switch (avatar.id) {
      case 'celestial':
        return {
          ...baseStyles,
          borderRadius: '50%',
          border: `2px solid ${avatar.accent}`,
          boxShadow: `0 0 15px ${avatar.accent}80, 0 0 30px ${avatar.accent}40`
        };
      case 'sentinel':
        return {
          ...baseStyles,
          borderRadius: '0',
          border: `2px solid ${avatar.accent}`,
          transform: `translate(-50%, -50%) rotate(45deg)`
        };
      case 'lumina':
        return {
          ...baseStyles,
          borderRadius: '50%',
          border: `2px solid ${avatar.accent}`,
          opacity: 0.8,
          filter: `blur(1px)`
        };
      case 'nexus':
        return {
          ...baseStyles,
          borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
          border: `2px solid ${avatar.accent}`,
          animation: 'pulse-shape 4s infinite alternate'
        };
      case 'astra':
        return {
          ...baseStyles,
          borderRadius: '50%',
          border: `2px solid ${avatar.accent}`,
          transform: `translate(-50%, -50%) scale(1.2)`,
          opacity: 0.7
        };
      case 'enigma':
        return {
          ...baseStyles,
          borderRadius: '50%',
          border: `2px solid ${avatar.accent}`,
          boxShadow: `0 0 15px ${avatar.accent}80, 0 0 30px ${avatar.accent}40`,
          animation: 'enigma-pulse 2s infinite alternate'
        };
      default:
        return baseStyles;
    }
  };
  return <>
      <style jsx global data-id="element-509">{`
        @keyframes pulse-shape {
          0% {
            border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%;
          }
          100% {
            border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
          }
        }
        @keyframes float-cursor {
          0%,
          100% {
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            transform: translate(-50%, -50%) scale(1.1);
          }
        }
        @keyframes enigma-pulse {
          0% {
            opacity: 0.6;
            transform: translate(-50%, -50%) scale(1) rotate(0deg);
          }
          50% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.2) rotate(180deg);
          }
          100% {
            opacity: 0.6;
            transform: translate(-50%, -50%) scale(1) rotate(360deg);
          }
        }
        .cursor-dot {
          z-index: 999;
          pointer-events: none;
          position: fixed;
          transform: translate(-50%, -50%);
          transition:
            width 0.3s ease,
            height 0.3s ease,
            background-color 0.3s ease;
        }
        .cursor-ring {
          z-index: 998;
          pointer-events: none;
          position: fixed;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: all 0.15s ease-out;
        }
        .not-allowed-cursor::before,
        .not-allowed-cursor::after {
          content: '';
          position: absolute;
          width: 2px;
          height: 100%;
          background: #ff0000;
          left: 50%;
          top: 0;
        }
        .not-allowed-cursor::before {
          transform: translateX(-50%) rotate(45deg);
        }
        .not-allowed-cursor::after {
          transform: translateX(-50%) rotate(-45deg);
        }
      `}</style>
      <div className="cursor-dot" style={{
      left: `${position.x}px`,
      top: `${position.y}px`,
      width: `${cursorSize.width / 4}px`,
      height: `${cursorSize.height / 4}px`,
      backgroundColor: cursorType === 'not-allowed' ? '#ff0000' : avatar?.accent || '#f5f5f5',
      boxShadow: `0 0 15px ${cursorType === 'not-allowed' ? '#ff0000' : avatar?.accent || '#f5f5f5'}`,
      borderRadius: '50%'
    }} data-id="element-510"></div>
      <div className={`cursor-ring ${cursorType === 'not-allowed' ? 'not-allowed-cursor' : ''}`} style={{
      left: `${position.x}px`,
      top: `${position.y}px`,
      width: `${cursorSize.width}px`,
      height: `${cursorSize.height}px`,
      ...(cursorType === 'not-allowed' ? {
        borderRadius: '50%',
        border: '2px solid #ff0000',
        boxShadow: '0 0 15px rgba(255, 0, 0, 0.5)',
        background: 'rgba(255, 0, 0, 0.1)'
      } : getCursorStyles())
    }} data-id="element-511"></div>
    </>;
};